import { createEffect, createEvent, createStore, sample } from 'effector';

import { isToday } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { $commonData } from '@stores/global-data';

const TIMEZONE = 'Asia/Vladivostok';
const STORAGE_KEY = 'toplineLastClosed';
const STORAGE_TEXT_KEY = 'toplineLastClosedText';

type ToplineState = {
    isOpen: boolean;
};

const defaultValue: ToplineState = {
    isOpen: false,
};

export const getToplineVisibilityFx = createEffect<string | null, ToplineState>(
    async (toplineText) => {
        const normalizedText = toplineText?.trim() ?? null;

        if (normalizedText === null || normalizedText === '') return { isOpen: false };

        const lastClosedDatetimeStr = localStorage.getItem(STORAGE_KEY);
        if (!lastClosedDatetimeStr) return { isOpen: true };

        const lastClosedText = localStorage.getItem(STORAGE_TEXT_KEY);
        if (lastClosedText !== null && lastClosedText !== normalizedText) {
            return { isOpen: true };
        }

        const lastClosedVladivostok = utcToZonedTime(new Date(lastClosedDatetimeStr), TIMEZONE);
        if (!isToday(lastClosedVladivostok)) {
            return { isOpen: true };
        }

        return { isOpen: false };
    },
);

const setToplineClosedFx = createEffect<string | null, void>(async (toplineText) => {
    const nowUtc = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, nowUtc);

    const normalizedText = toplineText?.trim() ?? null;
    if (normalizedText === null || normalizedText === '') {
        localStorage.removeItem(STORAGE_TEXT_KEY);
    } else {
        localStorage.setItem(STORAGE_TEXT_KEY, normalizedText);
    }
});

export const setToplineVisible = createEvent<boolean>();
export const toplineMounted = createEvent();

// Narrow projection so subscribers re-render only when the actual text changes.
// $commonData emits a fresh reference on every hydrate(), but .map skips emit
// when the projected primitive value is the same — preventing setState in
// already-mounted components during _app.tsx render.
export const $toplineText = $commonData.map((data) => data.settings.topline_text);

export const $isToplineVisible = createStore(defaultValue)
    .on(setToplineVisible, (_, payload) => ({ isOpen: payload }))
    .on(getToplineVisibilityFx.doneData, (_, { isOpen }) => ({ isOpen }));

sample({
    clock: setToplineVisible,
    source: $commonData,
    filter: (_, isOpen) => !isOpen,
    fn: (data) => data.settings.topline_text,
    target: setToplineClosedFx,
});

// Triggered from Topline's mount effect rather than from $commonData updates,
// so the side effect doesn't run synchronously during effector hydration in
// _app.tsx render — which caused "Cannot update a component while rendering
// a different component" React warnings.
sample({
    clock: toplineMounted,
    source: $commonData,
    fn: (data) => data.settings.topline_text,
    target: getToplineVisibilityFx,
});
